/**
 * threadListIdentity — the identity facts every thread LIST surface reads
 * (chat-thread experience PR-5, 2026-09-09): who is in a thread, what people
 * are called, and the viewer's own categories (names are personal — no
 * registry name rides here, 2026-09-09). One props bundle + two resolvers, so the rail, the chip
 * strip and the mobile sheet name and file a thread identically.
 */
import type { ChatThread } from './types';
import type { ThreadCategory } from '../../types/threadCategories';
import {
  resolveThreadDisplayKind,
  resolveThreadDisplayTitle,
  type ThreadDisplayKind,
} from '../../services/groupThreads/threadDisplayTitle';

export interface ThreadListIdentityProps {
  /**
   * threadId → registered participant uids, from the SAME membership read
   * that feeds `participantsByThread` (GlobalChatSystem's
   * `sharedThreadParticipantIds`). Names the other person in a direct
   * message; absent ⇒ no thread is a direct message (counts still section
   * group threads).
   */
  participantIdsByThread?: Record<string, string[]>;
  /** uid → display name (the viewer's roster, then server-resolved names). */
  memberLabels?: Record<string, string>;
  /** The viewer's personal categories (order = display order). */
  categories?: readonly ThreadCategory[] | null;
}

export function threadDisplayKindFor(
  thread: Pick<ChatThread, 'id' | 'sharedThread'>,
  identity: ThreadListIdentityProps | null | undefined,
  viewerUid: string | null | undefined,
  participantsByThread?: Record<string, number> | null,
): ThreadDisplayKind {
  return resolveThreadDisplayKind({
    participantIds: identity?.participantIdsByThread?.[thread.id] ?? null,
    participantCount: participantsByThread?.[thread.id] ?? null,
    selfId: viewerUid,
    sharedThread: thread.sharedThread === true,
  });
}

export function threadDisplayTitleFor(
  thread: Pick<ChatThread, 'id' | 'title' | 'userRenamed'>,
  identity: ThreadListIdentityProps | null | undefined,
  viewerUid: string | null | undefined,
  routineName?: string | null,
): string {
  return resolveThreadDisplayTitle({
    thread,
    selfId: viewerUid,
    participantIds: identity?.participantIdsByThread?.[thread.id] ?? null,
    memberLabels: identity?.memberLabels ?? null,
    routineName: routineName ?? null,
    fallback: thread.title,
  });
}
